import React, { FC, useState } from 'react'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import { Link, useNavigate } from 'react-router-dom'
import { useMediaQuery, useTheme, Checkbox, IconButton } from '@mui/material'
import useHeaderDetails from '@header/useHeaderDetails'
import useAdobeAnalytics from '@analytics/adobe'
import { PATIENTS } from '../../route.const'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { styled } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import {
  PRIVACY_POLICY_CONSENT_VALIDATION_MESSAGE,
  OPTUM_MATCH_POLICY_VERSION,
  ENABLE_OBGYN,
} from './../../constants'
import Alert from '@mui/material/Alert'
import useUser from '@context/UserContext/useUser'
import { PatientModel } from '@context/UserContext/typing'
import localStore from '@service/local-store'
import ErrorIcon from '@mui/icons-material/Error'
import data from '@config/patient/privacy-modal.json'
import { useFlags } from 'launchdarkly-react-client-sdk'

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '8px',
    width: '100%',
    maxWidth: '830px',
    margin: '16px',
    [theme.breakpoints.down('sm')]: {
      margin: 0,
      width: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
      borderRadius: 0,
      height: '100%',
    },
  },
  '& .MuiDialogTitle-root': {
    padding: '24px 24px 24px',
    fontSize: '24px',
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      padding: '50px 24px 24px',
    },
  },
  '& .BannerDialogContent-root': {
    padding: '0 24px 24px',
    '& p': {
      marginBottom: '24px',
    },
  },
  '& .MuiDialogActions-root': {
    padding: '0 24px 24px',
    flexDirection: 'column',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
    },
  },
}))

interface Actions {
  id: number
  text: string
  isRequired: boolean
  isChecked: boolean
}

interface TermsOfServiceModalProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  isCampaign?: boolean
}

const TermsOfServiceModal: FC<TermsOfServiceModalProps> = ({
  open,
  setOpen,
  isCampaign = false,
}) => {
  const { login, role } = useHeaderDetails()
  const { breakpoints } = useTheme()
  const isMobile = useMediaQuery(breakpoints.down('sm'))
  const { trackLink } = useAdobeAnalytics()
  const { setUser } = useUser()
  const { enableMultispeciality } = useFlags()
  const isOBGYNEnabled =
    enableMultispeciality !== undefined ? enableMultispeciality : ENABLE_OBGYN
  const navigateUrl = useNavigate()
  if (
    localStore.get('guestSurveySubmit') === null ||
    localStore.get('guestSurveySubmit') === undefined
  ) {
    if (!login) {
      localStore.set('zip', '')
    }
  }
  localStore.set('sms_consent', false)

  const handleDialogClose = () => {
    setOpen(false)
  }

  const [isValid, setIsValid] = useState(true)
  const [checkBoxValues, setCheckBoxValues] = useState<Actions[]>(
    data?.data || []
  )

  const handleCheckBoxOnchange = (id: number) => {
    const updatedList = checkBoxValues.map((item) => {
      if (item.id === id) {
        return { ...item, isChecked: !item.isChecked }
      }
      return item
    })
    setCheckBoxValues(updatedList)

    // Track checkbox toggle for campaign
    if (isCampaign) {
      const checkboxName = id === 1 ? 'age_attestation' : 'ospp_tou_attestation'
      trackLink(
        {
          name: `om_ocwa-virtual_pa_check_box_toggle:${checkboxName}`,
          location: 'om_ocwa-virtual_campaign_pa_terms_of_service_modal',
          type: 'internal',
        },
        {
          loginStatus: login ? 'logged in' : 'logged out',
          userType: role,
        }
      )
    }
  }

  const handleContinue = () => {
    const isAllChecked = checkBoxValues.every((item) =>
      item.isRequired ? item.isChecked : true
    )
    setIsValid(isAllChecked)
    if (isAllChecked) {
      localStore.set('policy_details_version', OPTUM_MATCH_POLICY_VERSION)
      localStore.set('is_OSPP_TOU_accepted', 'true')
      localStore.set('userType', 'guestUser')
      localStore.set('surveyState', 'Started')
      localStore.remove('auth_type')
      setUser({ answers: {} } as PatientModel, PATIENTS)
      localStore.set('loginEvent', 'true')
      if (!login) {
        localStore.set('userType', 'guestUser')
        setUser({ answers: {} } as PatientModel, PATIENTS)
      } else {
        localStore.set('userType', 'authUser')
      }

      // Track continue button for campaign
      if (isCampaign) {
        trackLink(
          {
            name: 'om_ocwa-virtual_cta_click:attestation_continue',
            location: 'om_ocwa-virtual_campaign_pa_terms_of_service_modal',
            type: 'internal',
          },
          {
            loginStatus: login ? 'logged in' : 'logged out',
            userType: role,
          }
        )
      } else {
        trackLink(
          {
            name: 'om_cta_click:attestation_continue',
            location: 'om_pa_update_terms_of_service_modal',
            type: 'internal',
          },
          {
            loginStatus: login ? 'logged in' : 'logged out',
            userType: role,
          }
        )
      }
      if (isOBGYNEnabled) {
        navigateUrl(`/patients/survey-selection`)
      } else {
        localStore.set('surveyType', 'Primary care')
        navigateUrl('/patients/begin-search')
      }
      handleDialogClose()
    }
  }

  return (
    <>
      {open && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignContent: 'center',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            padding: { xs: 0, sm: 2 },
            width: { sm: '100%', md: 'auto' },
          }}
        >
          <BootstrapDialog
            onClose={handleDialogClose}
            aria-labelledby="customized-dialog-title"
            open={open}
            aria-modal="true"
            disableEscapeKeyDown
            hideBackdrop={false}
            disableEnforceFocus
            style={{ pointerEvents: 'none' }}
            PaperProps={{ sx: { pointerEvents: 'auto', width: '100%' } }}
            BackdropProps={{
              style: { pointerEvents: 'auto' },
            }}
          >
            <DialogTitle sx={{ pt: { xs: 3, sm: 3, lg: 0 } }}>
              <Typography
                variant={isMobile ? 'h2' : 'h3'}
                component="div"
                color={'primary'}
              >
                Terms of service
              </Typography>
              <IconButton
                aria-label="close"
                onClick={() => {
                  trackLink(
                    {
                      name: isCampaign
                        ? 'om_ocwa-virtual_modal_dismiss:terms_of_service'
                        : 'om_modal_dismiss:terms_of_service',
                      location: isCampaign
                        ? 'om_ocwa-virtual_campaign_pa_terms_of_service_modal'
                        : 'om_pa_update_terms_of_service_modal',
                      type: 'internal',
                    },
                    {
                      loginStatus: login ? 'logged in' : 'logged out',
                      userType: role,
                    }
                  )
                  handleDialogClose()
                }}
                sx={({ ues }) => ({
                  position: 'absolute',
                  right: 15,
                  top: 20,
                  color: ues.primary['100'],
                })}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent
              className="BannerDialogContent-root"
              sx={{ marginLeft: '0' }}
            >
              <Typography variant="body2">
                Optum Match can only be used by individuals 18 years or older,
                and anyone under 18 who attempts to register will be exited from
                the platform.
              </Typography>
              <Typography variant="body2">
                Review the the Optum Match
                <Box
                  component={Link}
                  to={'/privacy-policy'}
                  target="_blank"
                  onClick={() => {
                    if (isCampaign) {
                      trackLink(
                        {
                          name: 'om_ocwa-virtual_pa_link_click:privacy_policy',
                          location:
                            'om_ocwa-virtual_campaign_pa_terms_of_service_modal',
                          type: 'internal',
                        },
                        {
                          loginStatus: login ? 'logged in' : 'logged out',
                          userType: role,
                        }
                      )
                    } else {
                      trackLink(
                        {
                          name: 'om_pa_link_click:privacy_policy',
                          location: 'om_pa_update_terms_of_service_modal',
                          type: 'internal',
                        },
                        {
                          loginStatus: login ? 'logged in' : 'logged out',
                          userType: role,
                        }
                      )
                    }
                  }}
                  sx={({ ues }) => ({
                    color: ues.primary[70],
                    textUnderlineOffset: '4px',
                  })}
                >
                  {' '}
                  Privacy policy{' '}
                </Box>
                and
                <Box
                  component={Link}
                  to={'/terms-of-use'}
                  target="_blank"
                  onClick={() => {
                    if (isCampaign) {
                      trackLink(
                        {
                          name: 'om_ocwa-virtual_pa_link_click:terms_of_use',
                          location:
                            'om_ocwa-virtual_campaign_pa_terms_of_service_modal',
                          type: 'internal',
                        },
                        {
                          loginStatus: login ? 'logged in' : 'logged out',
                          userType: role,
                        }
                      )
                    } else {
                      trackLink(
                        {
                          name: 'om_pa_link_click:terms_of_use',
                          location: 'om_pa_update_terms_of_service_modal',
                          type: 'internal',
                        },
                        {
                          loginStatus: login ? 'logged in' : 'logged out',
                          userType: role,
                        }
                      )
                    }
                  }}
                  sx={({ ues }) => ({
                    color: ues.primary[70],
                    textUnderlineOffset: '4px',
                  })}
                >
                  {' '}
                  Terms of use.
                </Box>
              </Typography>

              <Box
                sx={({ ues }) => ({
                  pb: 2,
                  pt: 1.5,
                  mb: 2,
                  borderRadius: 3,
                  backgroundColor: ues.primary['5'],
                })}
              >
                {checkBoxValues?.map((item, index) => (
                  <Box
                    key={index}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0 }}
                  >
                    <Checkbox
                      id={`chk${item.id}`}
                      checked={checkBoxValues[item.id - 1].isChecked}
                      onChange={() => {
                        handleCheckBoxOnchange(checkBoxValues[item.id - 1].id)

                        // Only track for non-campaign pages (campaign tracking is handled in handleCheckBoxOnchange)
                        if (!isCampaign) {
                          const checkboxName =
                            item.id === 1
                              ? 'om_pa_check_box_toggle:age_attestation'
                              : 'om_pa_check_box_toggle:ospp_tou_attestation'
                          trackLink(
                            {
                              name: checkboxName,
                              location: 'om_pa_update_terms_of_service_modal',
                              type: 'internal',
                            },
                            {
                              loginStatus: login ? 'logged in' : 'logged out',
                              userType: role,
                            }
                          )
                        }
                      }}
                    />
                    <Typography
                      variant={'body2'}
                      sx={{ cursor: 'pointer' }}
                      mt={0}
                      component={'label'}
                      htmlFor={`chk${item.id}`}
                      dangerouslySetInnerHTML={{ __html: item.text }}
                    />
                  </Box>
                ))}

                {!isValid && (
                  <Alert
                    id={`error1`}
                    color="error"
                    iconMapping={{
                      error: <ErrorIcon fontSize="inherit" />,
                    }}
                    severity="error"
                    variant="outlined"
                    sx={{ ml: 1.5, mr: 1.5, mt: 2.5, pt: 1.5, pb: 1.5 }}
                  >
                    {PRIVACY_POLICY_CONSENT_VALIDATION_MESSAGE}
                  </Alert>
                )}
              </Box>

              <Typography
                variant="body3"
                mt={2.5}
                sx={{
                  position: isMobile ? 'absolute' : 'static',
                  bottom: isMobile ? 80 : 'none',
                  textAlign: isMobile ? 'center' : 'left',
                  left: isMobile ? 22 : 'none',
                  right: isMobile ? 22 : 'none',
                  fontSize: isMobile ? '12px' : '14px',
                  lineHeight: isMobile ? '16px' : '20px',
                }}
              >
                By clicking continue, you acknowledge that matches are based on
                information that you and the healthcare provider have given to
                Optum. The outcome of any search is for informational purposes
                only and not all of the possible healthcare providers in your
                chosen geographic area.
              </Typography>
            </DialogContent>
            <Box
              sx={{
                display: 'flex',
                position: isMobile ? 'absolute' : 'static',
                bottom: isMobile ? 0 : 'none',
                flexDirection: 'row',
                left: 0,
                right: 0,
                padding: { md: 3.5, xs: 2 },
                gap: isMobile ? 1 : 0,
                justifyContent: 'flex-end', // Always align to the right
                float: 'right',
              }}
            >
              <Button
                fullWidth={isMobile}
                variant={'contained'}
                sx={{ pl: '24px', pr: '24px' }}
                onClick={handleContinue}
                disabled={
                  !checkBoxValues.every((item) =>
                    item.isRequired ? item.isChecked : true
                  )
                }
              >
                Continue
              </Button>
            </Box>
          </BootstrapDialog>
        </Box>
      )}
    </>
  )
}

export default TermsOfServiceModal
